import React from 'react';
import { Link } from 'react-router-dom';
import { Package, Tags, ClipboardList, MessageCircle, Hand, Trash2, Printer, Users } from 'lucide-react';

import useAdminProducts from '../../hooks/useAdminProducts';
import useAdminCategories from '../../hooks/useAdminCategories';
import useAdminUsers from '../../hooks/useAdminUsers';
import useAdminOrders from '../../hooks/useAdminOrders';
import useAdminChatConversations from '../../hooks/useAdminChatConversations';
import useAdminDeletedOrders from '../../hooks/useAdminDeletedOrders';
import usePrintableTemplates from '../../hooks/usePrintableTemplates';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';

function Stat({ Icon, label, value, activeLabel, inactiveLabel, sublabel, to, loading, ValueIcon, valueIconAlert }) {
  return (
    <Link to={to} className="ss-admin-stat">
      <Card>
        <div className="ss-admin-stat__head">
          <div className="ss-admin-stat__icon"><Icon size={22} aria-hidden="true" /></div>
          <div className="ss-admin-stat__label">{label}</div>
        </div>
        <div className="ss-admin-stat__value">
          {loading ? (
            <Spinner label="Loading" />
          ) : (
            <span className="ss-admin-stat__value-inner">
              {value}
              {ValueIcon && (
                <ValueIcon
                  size={20}
                  aria-hidden="true"
                  className={`ss-admin-stat__value-icon${valueIconAlert ? ' ss-admin-stat__value-icon--alert' : ''}`}
                />
              )}
            </span>
          )}
        </div>
        {activeLabel && !loading && (
          <div className="ss-admin-stat__sub">
            <span>{activeLabel}</span>
            <span>{inactiveLabel}</span>
          </div>
        )}
        {sublabel && !loading && (
          <div className="ss-admin-stat__sub" style={{ justifyContent: 'center' }}>
            <span>{sublabel}</span>
          </div>
        )}
      </Card>
    </Link>
  );
}

function StatBreakdown({ Icon, label, items, to, loading }) {
  return (
    <Link to={to} className="ss-admin-stat">
      <Card>
        <div className="ss-admin-stat__head">
          <div className="ss-admin-stat__icon"><Icon size={22} aria-hidden="true" /></div>
          <div className="ss-admin-stat__label">{label}</div>
        </div>
        {loading ? (
          <div className="ss-admin-stat__value"><Spinner label="Loading" /></div>
        ) : (
          <ul className="ss-admin-stat__breakdown">
            {items.map(({ label: itemLabel, value, gapBefore, alert }) => (
              <li key={itemLabel} className={gapBefore ? 'ss-admin-stat__breakdown-gap' : undefined}>
                <span className={alert && value > 0 ? 'ss-admin-stat__breakdown-alert' : undefined}>{itemLabel}</span>
                <span>{value}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Link>
  );
}

export default function AdminDashboard() {
  const products = useAdminProducts({ limit: 1 });
  const activeProducts = useAdminProducts({ limit: 1, includeInactive: false });
  const inactiveCount = products.total - activeProducts.total;
  const { categories, loading: catLoading } = useAdminCategories();
  const activeCategoryCount = categories.filter((c) => c.active).length;
  const inactiveCategoryCount = categories.length - activeCategoryCount;
  const shipPaid = useAdminOrders({ requiresShipping: true, paymentStatus: 'paid', limit: 1 });
  const shipPending = useAdminOrders({ requiresShipping: true, paymentStatus: 'pending', limit: 1 });
  const shipProcessing = useAdminOrders({ requiresShipping: true, status: 'processing', limit: 1 });
  const shipBalanceDue = useAdminOrders({ requiresShipping: true, paymentStatus: 'balance_due', limit: 1 });
  const shipLoading = shipPaid.loading || shipPending.loading || shipProcessing.loading || shipBalanceDue.loading;
  const pickupPaid = useAdminOrders({ requiresPickup: true, paymentStatus: 'paid', limit: 1 });
  const pickupBalanceDue = useAdminOrders({ requiresPickup: true, paymentStatus: 'balance_due', limit: 1 });
  const pickupPending = useAdminOrders({ requiresPickup: true, paymentStatus: 'pending', limit: 1 });
  const pickupProcessing = useAdminOrders({ requiresPickup: true, status: 'processing', limit: 1 });
  const pickupReady = useAdminOrders({ requiresPickup: true, status: 'ready_for_pickup', limit: 1 });
  const pickupLoading =
    pickupPaid.loading || pickupBalanceDue.loading || pickupPending.loading || pickupProcessing.loading || pickupReady.loading;
  const chatWaiting = useAdminChatConversations({ handoffRequested: true, limit: 1 });
  const chatActive = useAdminChatConversations({ status: 'active', limit: 1 });
  const chatArchived = useAdminChatConversations({ status: 'archived', limit: 1 });
  const chatLoading = chatWaiting.loading || chatActive.loading || chatArchived.loading;
  const deletedOrders = useAdminDeletedOrders({ limit: 1 });
  const printTemplates = usePrintableTemplates({ limit: 1 });
  const users = useAdminUsers({ limit: 1 });
  const activeUsers = useAdminUsers({ limit: 1, active: true });
  const inactiveUserCount = users.total - activeUsers.total;

  return (
    <div>
      <h1>Admin</h1>

      <div className="ss-admin-stats" style={{ marginTop: 'var(--ss-space-5)' }}>
        <StatBreakdown
          Icon={ClipboardList}
          label="Ship"
          items={[
            { label: 'Paid', value: shipPaid.total },
            { label: 'Balance Due', value: shipBalanceDue.total },
            { label: 'Pending', value: shipPending.total, gapBefore: true },
            { label: 'Processing', value: shipProcessing.total },
          ]}
          to="/admin/ship"
          loading={shipLoading}
        />
        <StatBreakdown
          Icon={ClipboardList}
          label="Pickups"
          items={[
            { label: 'Paid', value: pickupPaid.total },
            { label: 'Balance Due', value: pickupBalanceDue.total },
            { label: 'Pending', value: pickupPending.total, gapBefore: true },
            { label: 'Processing', value: pickupProcessing.total },
            { label: 'Ready for Pickup', value: pickupReady.total },
          ]}
          to="/admin/pickups"
          loading={pickupLoading}
        />
        <Stat
          Icon={MessageCircle}
          label="Chat"
          value={chatWaiting.total}
          activeLabel={`${chatActive.total} active`}
          inactiveLabel={`${chatArchived.total} closed`}
          to={chatWaiting.total > 0 ? '/admin/chat?handoff=1' : '/admin/chat'}
          loading={chatLoading}
          ValueIcon={Hand}
          valueIconAlert={chatWaiting.total > 0}
        />
        <Stat
          Icon={Package}
          label="Products"
          value={products.total}
          activeLabel={`${activeProducts.total} active`}
          inactiveLabel={`${inactiveCount} inactive`}
          to="/admin/products"
          loading={products.loading || activeProducts.loading}
        />
        <Stat
          Icon={Tags}
          label="Categories"
          value={categories.length}
          activeLabel={`${activeCategoryCount} active`}
          inactiveLabel={`${inactiveCategoryCount} inactive`}
          to="/admin/categories"
          loading={catLoading}
        />
        <Stat
          Icon={Users}
          label="Users"
          value={users.total}
          activeLabel={`${activeUsers.total} active`}
          inactiveLabel={`${inactiveUserCount} inactive`}
          to="/admin/users"
          loading={users.loading || activeUsers.loading}
        />
        <Stat
          Icon={Trash2}
          label="Deleted Orders"
          value={deletedOrders.total}
          to="/admin/deleted-orders"
          loading={deletedOrders.loading}
        />
        <Stat
          Icon={Printer}
          label="Print"
          value={printTemplates.total}
          sublabel={printTemplates.total === 1 ? 'template' : 'templates'}
          to="/admin/print"
          loading={printTemplates.loading}
        />
      </div>
    </div>
  );
}
